import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroCarousel from '../components/HeroCarousel';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { ArrowRight, Calendar, Package, Repeat } from 'lucide-react';
import { API, remapProductImageUrl, normalizeRenderableImageUrl } from '../lib/api';

const coerceNumber = (value, fallback = 0) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
};

const normalizeProduct = (product = {}) => ({
    ...product,
    price: coerceNumber(product.price, 0),
    compare_at_price: product.compare_at_price == null ? null : coerceNumber(product.compare_at_price, 0),
    images: Array.isArray(product.images) ? product.images.filter(Boolean).map(remapProductImageUrl) : [],
    main_image: remapProductImageUrl(product.main_image),
    stock_quantity: coerceNumber(product.stock_quantity, 0),
});

const normalizeFeature = (feature) => {
    if (typeof feature === 'string') {
        return { title: feature, description: '' };
    }

    if (feature && typeof feature === 'object') {
        return {
            title: feature.title || feature.name || 'Feature',
            description: feature.description || '',
        };
    }

    return null;
};

const normalizeFeaturedSubscription = (plan) => {
    if (!plan || typeof plan !== 'object') return null;

    const rawFeatures = Array.isArray(plan.homepage_features)
        ? plan.homepage_features
        : Array.isArray(plan.features)
            ? plan.features
            : [];

    return {
        ...plan,
        homepage_features: rawFeatures.map(normalizeFeature).filter(Boolean),
        homepage_image_url: normalizeRenderableImageUrl(plan.homepage_image_url),
        image: normalizeRenderableImageUrl(plan.image),
    };
};

const HomePage = () => {
    const [featuredProducts, setFeaturedProducts] = useState([]);
    const [featuredSub, setFeaturedSub] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const requests = [
                    axios.get(`${API}/products?featured=true`),
                    axios.get(`${API}/featured-subscription`),
                ];

                const [productsResult, subResult] = await Promise.allSettled(requests);

                if (!isMounted) return;

                if (productsResult.status === 'fulfilled') {
                    const products = Array.isArray(productsResult.value?.data)
                        ? productsResult.value.data.map(normalizeProduct)
                        : [];
                    setFeaturedProducts(products);
                } else {
                    console.error('Error fetching featured products:', productsResult.reason);
                    setFeaturedProducts([]);
                }

                if (subResult.status === 'fulfilled') {
                    setFeaturedSub(normalizeFeaturedSubscription(subResult.value?.data));
                } else {
                    console.error('Error fetching featured subscription:', subResult.reason);
                    setFeaturedSub(null);
                }
            } catch (error) {
                console.error('Error fetching homepage data:', error);
                if (isMounted) {
                    setFeaturedProducts([]);
                    setFeaturedSub(null);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <div className="min-h-screen bg-white" data-testid="home-page">
            <SEO
                title="Wayside Maple — Premium Small-Batch Maple Syrup from Troy, PA"
                description="Wayside Maple crafts premium small-batch maple syrup in the hills of Troy, Pennsylvania since 2009. Shop our pure maple syrup, seasonal subscription boxes, and artisan products."
                keywords="maple syrup, pure maple syrup, small batch, Troy Pennsylvania, Wayside Maple, artisan, farm-to-table, subscription box, seasonal"
                path="/"
            />
            <Header />
            
            <HeroCarousel />

            <div className="bg-maple-red py-3 sm:py-4 overflow-hidden">
                <div className="animate-marquee whitespace-nowrap flex">
                    <span className="font-anton text-xl text-white/90 mx-8">HANDCRAFTED IN PA</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">EST 2009</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">PURE MAPLE SYRUP</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">TROY PENNSYLVANIA</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">HANDCRAFTED IN PA</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">EST 2009</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">PURE MAPLE SYRUP</span>
                    <span className="text-white/50">•</span>
                    <span className="font-anton text-xl text-white/90 mx-8">TROY PENNSYLVANIA</span>
                    <span className="text-white/50">•</span>
                </div>
            </div>

            <section className="py-16 md:py-32" data-testid="featured-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 sm:mb-16 gap-4">
                        <div>
                            <span className="text-maple-red font-mono text-sm tracking-widest">
                                SHOP
                            </span>
                            <h2 className="font-anton text-3xl sm:text-5xl lg:text-6xl text-maple-dark mt-3 sm:mt-4 leading-none">
                                FEATURED PRODUCTS
                            </h2>
                        </div>
                        <Link 
                            to="/shop" 
                            className="mt-6 md:mt-0 flex items-center gap-2 text-maple-dark/60 hover:text-maple-red transition-colors group"
                            data-testid="view-all-products"
                        >
                            <span className="font-manrope uppercase tracking-widest text-sm">View All</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform" />
                        </Link>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
                            {[1, 2, 3].map(i => (
                                <div key={i} className="bg-maple-card animate-pulse aspect-[4/5]" />
                            ))}
                        </div>
                    ) : featuredProducts.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
                            {featuredProducts.slice(0, 3).map(product => (
                                <ProductCard key={product.id} product={product} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 border border-black/10">
                            <p className="text-black/40 font-mono">No featured products yet</p>
                            <Link to="/shop" className="text-maple-red hover:underline mt-4 inline-block">
                                Browse all products
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {featuredSub && featuredSub.homepage_visible !== false && (
            <section className="py-16 md:py-32 bg-maple-dark text-white" data-testid="subscription-section">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
                        <div>
                            <span className="text-maple-red font-mono text-sm tracking-widest">
                                {featuredSub.homepage_subtitle || 'FEATURED PLAN'}
                            </span>
                            <h2 className="font-anton text-3xl sm:text-5xl lg:text-6xl text-white mt-3 sm:mt-4 leading-none max-w-[12ch]">
                                {featuredSub.homepage_headline || featuredSub.name}
                            </h2>
                            <p className="text-white/75 text-base sm:text-lg mt-6 sm:mt-8 leading-relaxed max-w-2xl">
                                {featuredSub.homepage_description || featuredSub.description}
                            </p>
                            
                            {featuredSub.homepage_features?.length > 0 && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-8 sm:mt-10">
                                {featuredSub.homepage_features.map((feat, i) => (
                                <div key={`${feat.title}-${i}`} className="flex items-start gap-3">
                                    <div className="w-10 h-10 bg-white/10 flex items-center justify-center flex-shrink-0">
                                        {i === 0 ? <Calendar className="w-5 h-5 text-maple-red" /> :
                                         i === 1 ? <Package className="w-5 h-5 text-maple-red" /> :
                                         <Repeat className="w-5 h-5 text-maple-red" />}
                                    </div>
                                    <div>
                                        <h4 className="font-anton text-sm">{feat.title}</h4>
                                        {feat.description && (
                                            <p className="text-white/50 text-xs mt-1">{feat.description}</p>
                                        )}
                                    </div>
                                </div>
                                ))}
                            </div>
                            )}

                            <Link 
                                to={featuredSub.homepage_cta_link || '/shop'} 
                                className="btn-primary inline-block mt-10"
                                data-testid="subscription-cta"
                            >
                                <span>{featuredSub.homepage_cta_text || 'Learn More'}</span>
                            </Link>
                        </div>
                        {(featuredSub.homepage_image_url || featuredSub.image) && (
                        <div className="relative">
                            <div className="aspect-square overflow-hidden border border-white/10">
                                <img 
                                    src={featuredSub.homepage_image_url || featuredSub.image}
                                    alt={featuredSub.homepage_headline || featuredSub.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            {featuredSub.homepage_badge_text && (
                            <div className="static sm:absolute mt-4 sm:mt-0 sm:-bottom-6 sm:-left-6 bg-maple-red p-4 sm:p-6 md:p-8 inline-block max-w-[220px] sm:max-w-none">
                                <span className="font-mono text-2xl sm:text-4xl text-white">{featuredSub.homepage_badge_text}</span>
                                <p className="font-manrope text-sm text-white/80 mt-1">{featuredSub.homepage_badge_description}</p>
                            </div>
                            )}
                        </div>
                        )}
                    </div>
                </div>
            </section>
            )}

            <section className="py-16 md:py-32 bg-maple-secondary border-y border-black/10">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
                        <div>
                            <span className="text-maple-olive font-mono text-sm tracking-widest">
                                OUR STORY
                            </span>
                            <h2 className="font-anton text-4xl sm:text-5xl text-maple-dark mt-4">
                                FROM TREE TO TABLE
                            </h2>
                            <p className="text-maple-dark/60 text-lg mt-8 leading-relaxed">
                                Since 2009, we've been crafting premium maple syrup in the hills of Troy, 
                                Pennsylvania. Every bottle represents our commitment to traditional methods 
                                and uncompromising quality.
                            </p>
                            <Link 
                                to="/about" 
                                className="btn-secondary inline-block mt-10"
                                data-testid="about-teaser-link"
                            >
                                Learn More
                            </Link>
                        </div>
                        <div className="relative">
                            <div className="aspect-[4/3] overflow-hidden border border-black/10">
                                <img 
                                    src="/images/wayside-sugar-house.jpg"
                                    alt="Wayside Maple red sugar house in winter"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="absolute -bottom-6 -left-6 bg-maple-red p-6 md:p-8">
                                <span className="font-mono text-6xl text-white">15+</span>
                                <p className="font-manrope text-sm text-white/80 mt-2">Years of Excellence</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
                    <h2 className="font-anton text-4xl sm:text-5xl lg:text-6xl text-maple-dark">
                        TASTE THE TRADITION
                    </h2>
                    <p className="text-maple-dark/60 text-lg mt-6 max-w-2xl mx-auto">
                        Experience the pure, rich flavor of authentic Pennsylvania maple syrup.
                    </p>
                    <Link 
                        to="/shop" 
                        className="btn-primary inline-block mt-10"
                        data-testid="cta-shop-btn"
                    >
                        <span>Shop Now</span>
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default HomePage;
